import { useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { Key, MoreHorizontal, Pause, Play, Trash2, X } from "lucide-react";
import { toast } from "sonner";

import { ApiError } from "~/lib/api";
import {
    type RoleSlug,
    type UserRecord,
    useDeleteUser,
    useResetUserPassword,
    useUpdateUser,
    ALL_ROLES,
} from "~/lib/users";

import { CopyButton } from "./CopyButton";

interface UserRowActionsProps {
    user: UserRecord;
    isSelf: boolean;
}

/**
 * Row-level actions for a user in the /users table.
 *
 * Layered: a Radix DropdownMenu for the action picker, plus modal Dialogs
 * for confirmation (delete) and password disclosure (reset). Self-targeting
 * destructive actions are blocked so the admin can't lock themselves out.
 */
export function UserRowActions({ user, isSelf }: UserRowActionsProps): React.ReactElement {
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [confirmRoles, setConfirmRoles] = useState(false);
    const [editingRoles, setEditingRoles] = useState<RoleSlug[]>(user.roles);
    const [resetReveal, setResetReveal] = useState<string | null>(null);

    const update = useUpdateUser();
    const remove = useDeleteUser();
    const reset = useResetUserPassword();

    async function toggleStatus(): Promise<void> {
        const next = user.status === "active" ? "suspended" : "active";
        try {
            await update.mutateAsync({ id: user.id, status: next });
            toast.success(next === "active" ? "User reactivated" : "User suspended");
        } catch (err) {
            toast.error(err instanceof ApiError ? err.message : "Failed to update user");
        }
    }

    async function saveRoles(): Promise<void> {
        if (editingRoles.length === 0) {
            toast.error("Select at least one role");
            return;
        }
        try {
            await update.mutateAsync({ id: user.id, roles: editingRoles });
            toast.success("Roles updated");
            setConfirmRoles(false);
        } catch (err) {
            toast.error(err instanceof ApiError ? err.message : "Failed to update roles");
        }
    }

    async function doReset(): Promise<void> {
        try {
            const r = await reset.mutateAsync(user.id);
            setResetReveal(r.temporaryPassword);
        } catch (err) {
            toast.error(err instanceof ApiError ? err.message : "Failed to reset password");
        }
    }

    async function doDelete(): Promise<void> {
        try {
            await remove.mutateAsync(user.id);
            toast.success("User deleted");
            setConfirmDelete(false);
        } catch (err) {
            toast.error(err instanceof ApiError ? err.message : "Failed to delete user");
        }
    }

    return (
        <>
            <DropdownMenu.Root>
                <DropdownMenu.Trigger asChild>
                    <button type="button" className="btn btn--ghost btn--sm" aria-label="Row actions">
                        <MoreHorizontal size={16} />
                    </button>
                </DropdownMenu.Trigger>
                <DropdownMenu.Portal>
                    <DropdownMenu.Content
                        align="end"
                        sideOffset={4}
                        className="surface-raised enter-up z-popover"
                        style={{ minWidth: 200, padding: "var(--space-1)" }}
                    >
                        <DropdownMenu.Item
                            className="cmdbar__item"
                            onSelect={() => {
                                setEditingRoles(user.roles);
                                setConfirmRoles(true);
                            }}
                        >
                            <Key size={14} aria-hidden />
                            <span>Edit roles…</span>
                        </DropdownMenu.Item>
                        <DropdownMenu.Item
                            className="cmdbar__item"
                            onSelect={toggleStatus}
                            disabled={isSelf}
                        >
                            {user.status === "active" ? (
                                <>
                                    <Pause size={14} aria-hidden /> <span>Suspend</span>
                                </>
                            ) : (
                                <>
                                    <Play size={14} aria-hidden /> <span>Reactivate</span>
                                </>
                            )}
                        </DropdownMenu.Item>
                        <DropdownMenu.Item className="cmdbar__item" onSelect={doReset}>
                            <Key size={14} aria-hidden />
                            <span>Reset password…</span>
                        </DropdownMenu.Item>
                        <DropdownMenu.Separator className="divider" style={{ margin: "var(--space-1) 0" }} />
                        <DropdownMenu.Item
                            className="cmdbar__item text-danger"
                            disabled={isSelf}
                            onSelect={() => setConfirmDelete(true)}
                        >
                            <Trash2 size={14} aria-hidden />
                            <span>Delete user…</span>
                        </DropdownMenu.Item>
                    </DropdownMenu.Content>
                </DropdownMenu.Portal>
            </DropdownMenu.Root>

            {/* Edit roles modal */}
            <Dialog.Root open={confirmRoles} onOpenChange={setConfirmRoles}>
                <Dialog.Portal>
                    <Dialog.Overlay className="dialog-overlay">
                    <Dialog.Content className="dialog-content enter-up" style={modalStyle}>
                        <div className="cluster-xs" style={{ justifyContent: "space-between" }}>
                            <Dialog.Title className="type-h2">Roles for {user.email}</Dialog.Title>
                            <Dialog.Close asChild>
                                <button type="button" className="btn btn--ghost btn--sm" aria-label="Close">
                                    <X size={14} />
                                </button>
                            </Dialog.Close>
                        </div>
                        <div className="stack" style={{ marginTop: "var(--space-4)" }}>
                            <div className="cluster-xs">
                                {ALL_ROLES.map((role) => {
                                    const active = editingRoles.includes(role);
                                    return (
                                        <button
                                            key={role}
                                            type="button"
                                            onClick={() =>
                                                setEditingRoles((cur) =>
                                                    cur.includes(role) ? cur.filter((r) => r !== role) : [...cur, role],
                                                )
                                            }
                                            className={`chip ${active ? "chip--accent" : ""}`}
                                            style={{ cursor: "pointer", height: 26, opacity: active ? 1 : 0.6 }}
                                            aria-pressed={active}
                                        >
                                            {role}
                                        </button>
                                    );
                                })}
                            </div>
                            <button
                                type="button"
                                className="btn btn--primary press-crush"
                                onClick={saveRoles}
                                disabled={update.isPending}
                            >
                                {update.isPending ? "Saving…" : "Save roles"}
                            </button>
                        </div>
                    </Dialog.Content>
                    </Dialog.Overlay>
                </Dialog.Portal>
            </Dialog.Root>

            {/* Reset reveal modal */}
            <Dialog.Root open={resetReveal !== null} onOpenChange={(o) => !o && setResetReveal(null)}>
                <Dialog.Portal>
                    <Dialog.Overlay className="dialog-overlay">
                    <Dialog.Content className="dialog-content enter-up" style={modalStyle}>
                        <Dialog.Title className="type-h2">Temporary password</Dialog.Title>
                        <Dialog.Description className="text-text-muted text-13" style={{ marginTop: "var(--space-2)" }}>
                            Share this with <strong className="text-text">{user.email}</strong>. Shown once.
                        </Dialog.Description>
                        {resetReveal && (
                            <div
                                className="surface"
                                style={{
                                    padding: "var(--space-3)",
                                    marginTop: "var(--space-4)",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "var(--space-3)",
                                    fontFamily: "var(--font-family-mono)",
                                }}
                            >
                                <span style={{ flex: 1, wordBreak: "break-all" }}>{resetReveal}</span>
                                <CopyButton value={resetReveal} />
                            </div>
                        )}
                        <button
                            type="button"
                            className="btn btn--primary press-crush"
                            style={{ marginTop: "var(--space-4)" }}
                            onClick={() => setResetReveal(null)}
                        >
                            Done
                        </button>
                    </Dialog.Content>
                    </Dialog.Overlay>
                </Dialog.Portal>
            </Dialog.Root>

            {/* Delete confirmation modal */}
            <Dialog.Root open={confirmDelete} onOpenChange={setConfirmDelete}>
                <Dialog.Portal>
                    <Dialog.Overlay className="dialog-overlay">
                    <Dialog.Content className="dialog-content enter-up" style={modalStyle}>
                        <Dialog.Title className="type-h2">Delete user?</Dialog.Title>
                        <Dialog.Description className="text-text-muted text-13" style={{ marginTop: "var(--space-2)" }}>
                            <strong className="text-text">{user.email}</strong> will be permanently removed along with
                            their sessions and MFA factors. The audit log retains a record of their past activity.
                        </Dialog.Description>
                        <div className="cluster-xs" style={{ marginTop: "var(--space-4)", justifyContent: "flex-end" }}>
                            <Dialog.Close asChild>
                                <button type="button" className="btn btn--ghost">Cancel</button>
                            </Dialog.Close>
                            <button
                                type="button"
                                className="btn btn--danger press-crush"
                                onClick={doDelete}
                                disabled={remove.isPending}
                            >
                                {remove.isPending ? "Deleting…" : "Delete"}
                            </button>
                        </div>
                    </Dialog.Content>
                    </Dialog.Overlay>
                </Dialog.Portal>
            </Dialog.Root>
        </>
    );
}

const modalStyle: React.CSSProperties = {
    padding: "var(--space-6)",
};
